import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { store } from '../redux/store';
import { fetchAdApprovalAccounts } from '../redux/clientAdApprovalSlice';
export const useAccountSelection = ({ selectedId, enabled = true, }) => {
    const dispatch = useDispatch();
    const [connectionId, setConnectionId] = useState('');
    const [accountOptions, setAccountOptions] = useState([]);
    const [accountLoading, setAccountLoading] = useState(false);
    // ✅ Track last fetched ID to prevent duplicate/loop fetches
    const lastFetchedId = useRef('');
    // ✅ Prevent state updates if component unmounts mid-fetch
    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);
    useEffect(() => {
        // ✅ Guard 1: Skip if disabled or no ID
        if (!selectedId || !enabled) {
            setConnectionId('');
            setAccountOptions([]);
            lastFetchedId.current = '';
            return;
        }
        // ✅ Guard 2: Skip if we already fetched for this exact selectedId
        if (lastFetchedId.current === selectedId) {
            return;
        }
        // ✅ Guard 3: Reset state immediately when selectedId changes
        setConnectionId('');
        setAccountOptions([]);
        // Mark as in-progress for this ID
        lastFetchedId.current = selectedId;
        let cancelled = false;
        const fetchAccounts = async () => {
            setAccountLoading(true);
            try {
                const result = await dispatch(fetchAdApprovalAccounts({ clientId: selectedId }));
                // ✅ Bail out if selectedId changed while we were fetching
                if (cancelled || !isMounted.current)
                    return;
                const options = result?.payload?.options || [];
                setAccountOptions(options);
                // ✅ Read cache once at call time — not reactive
                const currentCache = store.getState().utility?.cache;
                const cachedAccountId = currentCache?.accountId ?? null;
                const cachedIsValid = cachedAccountId &&
                    options.some((o) => o.value === cachedAccountId);
                setConnectionId((prev) => {
                    const resolvedId = cachedIsValid ? cachedAccountId : (options[0]?.value || '');
                    if (prev === resolvedId)
                        return prev;
                    return resolvedId;
                });
            }
            catch {
                if (cancelled || !isMounted.current)
                    return;
                setAccountOptions([]);
                setConnectionId('');
            }
            finally {
                if (isMounted.current) {
                    setAccountLoading(false);
                }
            }
        };
        fetchAccounts();
        // ✅ Cleanup: mark as cancelled if selectedId changes before fetch completes
        return () => {
            cancelled = true;
        };
        // ✅ dispatch is intentionally excluded — it's stable and including it caused loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, enabled]);
    const currentAccountLabel = useMemo(() => accountOptions.find((o) => o.value === connectionId)?.label ||
        'Select Account', [accountOptions, connectionId]);
    const showAccountField = accountOptions.length > 1;
    return {
        connectionId,
        setConnectionId,
        accountOptions,
        accountLoading,
        currentAccountLabel,
        showAccountField,
    };
};
